import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Zap, ShieldCheck } from 'lucide-react';
import { PlatformLogo } from './PlatformLogo';

const Hero = () => {
  return (
    <section className="relative overflow-hidden radial-glow noise-bg">
      <div className="absolute inset-0 grid-pattern opacity-50" />
      <div className="absolute -top-24 -right-24 w-[500px] h-[500px] bg-[#ff8247] rounded-full blur-[200px] opacity-25" />
      <div className="absolute -bottom-32 -left-24 w-[500px] h-[500px] bg-[#ff2d6f] rounded-full blur-[200px] opacity-20" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6">
            <span className="pulse-dot" />
            <span className="text-xs font-semibold text-white/80 tracking-wide">1 247 commandes livrées aujourd’hui</span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.02] mb-6">
            Boostez votre <span className="gradient-text">visibilité</span><br />
            sans compromis.
          </h1>

          <p className="text-lg text-white/60 max-w-xl mb-8 leading-relaxed">
            Abonnés, likes et vues réels et progressifs pour TikTok, Instagram, YouTube, Twitch et plus. Livraison express en 12h, 100% sécurisé, sans mot de passe.
          </p>

          <div className="flex flex-wrap items-center gap-4 mb-10">
            <Link to="/collections" className="btn-primary inline-flex items-center gap-2 text-sm">
              Découvrir nos packs <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#reviews" className="btn-ghost inline-flex items-center gap-2 text-sm">
              Voir les avis clients
            </a>
          </div>

          <div className="flex flex-wrap gap-6 pt-6 border-t border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#ff8247]" />
              </div>
              <div>
                <div className="text-white font-semibold text-sm">Livraison 12h</div>
                <div className="text-white/50 text-xs">Progressive & naturelle</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-[#ff8247]" />
              </div>
              <div>
                <div className="text-white font-semibold text-sm">Sans mot de passe</div>
                <div className="text-white/50 text-xs">Compte 100% sécurisé</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                <Star className="w-5 h-5 text-[#ff8247]" fill="#ff8247" />
              </div>
              <div>
                <div className="text-white font-semibold text-sm">4.8 / 5 Trustpilot</div>
                <div className="text-white/50 text-xs">+12 000 avis vérifiés</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: '150ms' }}>
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-white/10">
            <img src="https://images.unsplash.com/photo-1576176243977-7c62b6472a31?w=1000&q=85" alt="Boost réseaux sociaux" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/30 to-transparent" />

            <div className="absolute top-5 left-5 right-5 flex justify-between">
              <div className="px-3 py-1.5 rounded-full bg-black/50 backdrop-blur border border-white/10 flex items-center gap-2">
                <span className="pulse-dot" />
                <span className="text-white text-xs font-semibold">EN DIRECT</span>
              </div>
              <div className="px-3 py-1.5 rounded-full bg-[#ff8247] flex items-center gap-1">
                <Star className="w-3 h-3 text-[#0a0a0f]" fill="#0a0a0f" />
                <span className="text-[#0a0a0f] text-xs font-bold">4.8</span>
              </div>
            </div>

            <div className="absolute bottom-5 left-5 right-5 glass-card rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/60 text-xs font-medium">Abonnés livrés ce mois</span>
                <span className="text-[#ff8247] text-xs font-semibold">+12.4%</span>
              </div>
              <div className="flex items-end justify-between">
                <span className="text-white text-3xl font-bold">5.2M</span>
                <div className="flex gap-1">
                  {[30, 55, 40, 70, 60, 85, 100].map((h, i) => (
                    <div key={i} className="w-1.5 rounded-full bg-gradient-to-t from-[#ff8247] to-[#ff2d6f]" style={{ height: `${h * 0.35}px` }} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="absolute -bottom-6 -left-6 glass-card rounded-2xl p-3 flex items-center gap-3 w-56 hidden md:flex">
            <div className="w-10 h-10 rounded-xl bg-[#FF0050]/15 flex items-center justify-center">
              <PlatformLogo platform="tiktok" className="w-6 h-6" color="#FF0050" />
            </div>
            <div>
              <div className="text-white text-xs font-semibold">+10 000 TikTok</div>
              <div className="text-white/50 text-[10px]">Livraison en cours…</div>
            </div>
          </div>

          <div className="absolute -top-4 -right-4 glass-card rounded-2xl p-3 flex items-center gap-3 w-56 hidden md:flex">
            <div className="w-10 h-10 rounded-xl bg-[#4285F4]/15 flex items-center justify-center">
              <PlatformLogo platform="google" className="w-6 h-6" />
            </div>
            <div>
              <div className="text-white text-xs font-semibold">+25 avis Google 5★</div>
              <div className="text-white/50 text-[10px]">Vérifiés & progressifs</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
